// 메트로놈 전체 화면 시트 — 헤더 아이콘으로 열고, 닫아도 재생은 계속된다(미니바로 이어짐)
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, ButtonBase, IconButton, Slide, Slider, Switch, Typography,
} from '@mui/material';
import Dialog from '../components/AppDialog';
import {
  ExpandMoreRounded, PlayArrowRounded, PauseRounded, EditOutlined, AddRounded, CheckRounded,
} from '@mui/icons-material';
import {
  useMetronome, useMetronomeClock, useBeat, configForSignature, configToPiece, formatClock,
} from './MetronomeProvider';
import {
  QUICK_SIGNATURES, BEAT_UNITS, MAX_BEATS, groupingOptions, defaultAccents, tempoName, groupingKey,
} from './timeSignatures';
import BeatRow from './BeatRow';
import Stepper from './Stepper';
import PieceDialog from './PieceDialog';

const SlideUp = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const SUBDIVISIONS = [['1', '♩'], ['2', '♫ 8분'], ['3', '셋잇단'], ['4', '16분'], ['swing', '스윙']];
const SOUNDS = [['click', '클릭'], ['wood', '우드블록'], ['bell', '벨'], ['beep', '전자음']];
const LINK_MODES = [['ask', '물어보기'], ['auto', '자동 저장'], ['off', '연동 안 함']];

function SectionLabel({ children, sx }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mt: 2.5, mb: 1, ...sx }}>
      {children}
    </Typography>
  );
}

// 고정 칸 수 선택 버튼 그리드 (줄바꿈 없이 칸 폭만 줄어든다)
function ChoiceGrid({ options, value, onChange, columns }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns || options.length}, minmax(0, 1fr))`, gap: '6px' }}>
      {options.map(([key, label]) => {
        const selected = key === value;
        return (
          <ButtonBase
            key={key}
            onClick={() => onChange(key)}
            aria-pressed={selected}
            sx={{
              height: 36,
              borderRadius: '10px',
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              bgcolor: selected ? 'primary.main' : 'transparent',
              color: selected ? 'primary.contrastText' : 'text.primary',
              fontSize: '0.8125rem',
              fontWeight: selected ? 700 : 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              px: 0.5,
            }}
          >
            {label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

function OptionRow({ title, hint, checked, onToggle, children }) {
  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', py: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>{title}</Typography>
          {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        </Box>
        {onToggle && <Switch color="secondary" checked={checked} onChange={(e) => onToggle(e.target.checked)} />}
      </Box>
      {checked && children && <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>{children}</Box>}
    </Box>
  );
}

function ParamRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pl: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {children}
    </Box>
  );
}

export default function MetronomeSheet() {
  const m = useMetronome();
  const elapsed = useMetronomeClock();
  const { config, setConfig, groups } = m;
  const [customOpen, setCustomOpen] = useState(false);
  const [flash, setFlash] = useState(false);
  const [pieceDialog, setPieceDialog] = useState(null); // { piece } | { preset }
  const [voiceInfo, setVoiceInfo] = useState(null);
  const tapsRef = useRef([]);

  const isQuick = QUICK_SIGNATURES.some(([b, u]) => b === config.beats && u === config.unit);
  const showCustom = customOpen || !isQuick;
  const options = groupingOptions(config.beats, config.unit);
  const currentGroupKey = groupingKey(groups) || String(config.beats);

  // 곡 편집 창을 띄운 채 뒤로가기로 시트가 닫히면, 다음에 열 때 편집 창이 다시 뜨지 않게 정리
  useEffect(() => {
    if (!m.sheetOpen) setPieceDialog(null);
  }, [m.sheetOpen]);

  useBeat((ev) => {
    if (!m.sheetOpen || !config.flash || ev.beat !== 0 || ev.muted) return;
    setFlash(true);
    setTimeout(() => setFlash(false), 90);
  });

  // 음성 카운트를 고르면 미리 불러와 "이 템포에서 음성이 들어가는지"를 안내한다
  useEffect(() => {
    if (config.voice === 'off') {
      setVoiceInfo(null);
      return;
    }
    let alive = true;
    m.engine.loadVoice()
      .then((voice) => {
        if (!alive) return;
        const clips = voice.clips[config.voice].slice(0, config.beats);
        const longest = Math.max(...clips.map(([, d]) => d));
        setVoiceInfo({ maxBpm: Math.floor((60 * 1.05) / longest) });
      })
      .catch(() => alive && setVoiceInfo({ error: true }));
    return () => { alive = false; };
  }, [config.voice, config.beats, m.engine]);

  const set = (patch) => setConfig((c) => ({ ...c, ...patch }));
  const setBpm = (bpm) => set({ bpm: Math.min(250, Math.max(30, Math.round(bpm))) });

  const tap = () => {
    const now = performance.now();
    const taps = tapsRef.current.filter((t) => now - t < 2500);
    taps.push(now);
    tapsRef.current = taps;
    if (taps.length >= 2) setBpm(60000 / ((taps[taps.length - 1] - taps[0]) / (taps.length - 1)));
  };

  const chooseSignature = (beats, unit) => {
    setCustomOpen(false);
    setConfig((c) => configForSignature(c, beats, unit));
  };

  const chooseGrouping = (g) => set({ grouping: g.length > 1 ? g.join('+') : null, accents: defaultAccents(g) });

  const selectedPiece = m.pieces.find((p) => p.id === config.pieceId);
  const presetOf = configToPiece(config);
  const pieceDirty = selectedPiece && (
    selectedPiece.bpm !== presetOf.bpm || selectedPiece.beats !== presetOf.beats
    || selectedPiece.beat_unit !== presetOf.beat_unit || (selectedPiece.grouping || null) !== (presetOf.grouping || null)
    || (selectedPiece.accents || '') !== presetOf.accents || selectedPiece.subdivision !== presetOf.subdivision
  );

  return (
    <Dialog
      fullScreen
      open={m.sheetOpen}
      onClose={m.closeSheet}
      TransitionComponent={SlideUp}
      // 스크롤은 Paper가 하므로 바탕색도 Paper에 준다. 안쪽 Box에만 주면 첫 화면 높이 아래로
      // 다크 모드 Paper의 elevation 오버레이(밝은 회색)가 드러나 배경이 두 톤으로 갈린다.
      PaperProps={{ sx: { bgcolor: 'background.default', backgroundImage: 'none' } }}
    >
      <Box sx={{ position: 'relative' }}>
        {/* 첫 박 화면 플래시 */}
        <Box
          sx={{
            position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 2,
            boxShadow: (t) => `inset 0 0 0 5px ${t.palette.secondary.main}`,
            opacity: flash ? 1 : 0, transition: flash ? 'none' : 'opacity 0.15s',
          }}
        />
        <Box
          sx={{
            position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper',
            borderBottom: '1px solid', borderColor: 'divider',
            pt: 'env(safe-area-inset-top, 0px)',
          }}
        >
          <Box sx={{ height: 56, display: 'flex', alignItems: 'center', px: 1, maxWidth: 520, mx: 'auto' }}>
            <IconButton onClick={m.closeSheet} aria-label="메트로놈 닫기">
              <ExpandMoreRounded />
            </IconButton>
            <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '1.0625rem' }}>메트로놈</Typography>
            <Typography sx={{ fontVariantNumeric: 'tabular-nums', color: m.playing ? 'text.primary' : 'text.secondary', pr: 1.5, fontWeight: 600 }}>
              {formatClock(elapsed)}
              {config.timer.on && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> / {config.timer.minutes}분</Box>}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 520, mx: 'auto', px: 2, pb: 'calc(32px + env(safe-area-inset-bottom, 0px))' }}>
          {/* 템포 */}
          <Box sx={{ textAlign: 'center', pt: 2 }}>
            <Typography sx={{ color: 'secondary.main', fontWeight: 700, fontSize: '0.875rem' }}>{tempoName(config.bpm)}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Stepper
                label="템포"
                value={config.bpm}
                min={30}
                max={250}
                onChange={setBpm}
                width={112}
                format={(v) => (
                  <Box component="span" sx={{ fontSize: '3.25rem', fontWeight: 800, lineHeight: 1.15, display: 'block' }}>{v}</Box>
                )}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">BPM · 버튼을 누르고 있으면 빠르게 바뀌어요</Typography>
            <Slider
              value={config.bpm}
              min={30}
              max={250}
              onChange={(_, v) => setBpm(v)}
              color="secondary"
              aria-label="템포"
              sx={{ mt: 1 }}
            />
          </Box>

          {/* 박별 강세 */}
          <SectionLabel sx={{ mt: 1 }}>박별 강세 · 탭하면 강 → 중 → 약 → 무음</SectionLabel>
          <BeatRow
            beats={config.beats}
            groups={groups}
            accents={config.accents}
            playing={m.playing}
            onChange={(accents) => set({ accents })}
          />

          {/* 재생 */}
          <Box sx={{ display: 'flex', gap: 1.25, mt: 2.5 }}>
            <Button variant="outlined" onClick={tap} sx={{ flex: 1, height: 56, fontWeight: 800, fontSize: '1rem', borderRadius: '14px' }}>
              TAP
            </Button>
            <Button
              variant="contained"
              onClick={m.toggle}
              startIcon={m.playing ? <PauseRounded /> : <PlayArrowRounded />}
              sx={{ flex: 2, height: 56, fontWeight: 800, fontSize: '1.0625rem', borderRadius: '14px' }}
            >
              {m.playing ? '정지' : '시작'}
            </Button>
          </Box>

          {/* 박자 */}
          <SectionLabel>박자</SectionLabel>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '6px' }}>
            {QUICK_SIGNATURES.map(([b, u]) => {
              const selected = !showCustom && config.beats === b && config.unit === u;
              return (
                <ButtonBase
                  key={`${b}/${u}`}
                  onClick={() => chooseSignature(b, u)}
                  aria-pressed={selected}
                  sx={{
                    height: 36, borderRadius: '10px', border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                    color: selected ? 'primary.contrastText' : 'text.primary',
                    fontWeight: selected ? 700 : 500, fontSize: '0.875rem',
                  }}
                >
                  {b}/{u}
                </ButtonBase>
              );
            })}
            <ButtonBase
              onClick={() => setCustomOpen(true)}
              aria-pressed={showCustom}
              sx={{
                height: 36, borderRadius: '10px', border: '1px solid',
                borderColor: showCustom ? 'primary.main' : 'divider',
                bgcolor: showCustom ? 'primary.main' : 'transparent',
                color: showCustom ? 'primary.contrastText' : 'text.primary',
                fontWeight: showCustom ? 700 : 500, fontSize: '0.8125rem',
              }}
            >
              {showCustom && !isQuick ? `${config.beats}/${config.unit}` : '직접'}
            </ButtonBase>
          </Box>
          {showCustom && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center', mt: 1.5, p: 1.25, borderRadius: '12px', bgcolor: 'action.hover' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">박 수</Typography>
                <Box>
                  <Stepper
                    label="박 수"
                    value={config.beats}
                    min={1}
                    max={MAX_BEATS}
                    onChange={(v) => setConfig((c) => configForSignature(c, v, c.unit))}
                  />
                </Box>
              </Box>
              <Typography sx={{ fontSize: '1.5rem', color: 'text.secondary', mt: 2 }}>/</Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">음표</Typography>
                <Box>
                  <Stepper
                    label="박 단위"
                    value={BEAT_UNITS.indexOf(config.unit)}
                    min={0}
                    max={BEAT_UNITS.length - 1}
                    format={(i) => BEAT_UNITS[i]}
                    onChange={(i) => setConfig((c) => configForSignature(c, c.beats, BEAT_UNITS[i]))}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* 묶음 — 경우의 수가 많으면 줄바꿈 대신 옆으로 밀어 본다 */}
          {options.length > 1 && (
            <>
              <SectionLabel>묶음</SectionLabel>
              <Box
                sx={{
                  display: 'flex', gap: '6px', overflowX: 'auto', mx: -2, px: 2, pb: 0.5,
                  scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {options.map((g) => {
                  const key = g.join('+');
                  const selected = key === currentGroupKey;
                  return (
                    <ButtonBase
                      key={key}
                      onClick={() => chooseGrouping(g)}
                      aria-pressed={selected}
                      sx={{
                        flex: 'none', height: 32, px: 1.5, borderRadius: '16px', border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        bgcolor: selected ? 'primary.main' : 'transparent',
                        color: selected ? 'primary.contrastText' : 'text.primary',
                        fontWeight: selected ? 700 : 500, fontSize: '0.8125rem', whiteSpace: 'nowrap',
                      }}
                    >
                      {g.length === 1 ? '묶음 없음' : key}
                    </ButtonBase>
                  );
                })}
              </Box>
            </>
          )}

          <SectionLabel>세분</SectionLabel>
          <ChoiceGrid options={SUBDIVISIONS} value={config.subdivision} onChange={(subdivision) => set({ subdivision })} />

          <SectionLabel>소리</SectionLabel>
          <ChoiceGrid options={SOUNDS} value={config.sound} onChange={(sound) => set({ sound })} />

          <Box sx={{ mt: 1.5 }}>
            <OptionRow
              title="음성 카운트"
              hint={
                <Box component="span" sx={{ color: voiceInfo && (voiceInfo.error || config.bpm > voiceInfo.maxBpm) ? 'warning.main' : undefined }}>
                  {!voiceInfo
                    ? '"하나 둘 셋"으로 세어 줘요'
                    : voiceInfo.error
                      ? '음성을 불러오지 못해 클릭으로 울려요'
                      : config.bpm > voiceInfo.maxBpm
                        ? `템포가 빨라 지금은 클릭으로 울려요 · ♩=${voiceInfo.maxBpm}까지`
                        : `"하나 둘 셋"으로 세어 줘요 · ♩=${voiceInfo.maxBpm}까지`}
                </Box>
              }
              checked={config.voice !== 'off'}
              onToggle={(on) => set({ voice: on ? 'native' : 'off' })}
            />
          </Box>

          {/* 연습 옵션 */}
          <SectionLabel>연습 옵션</SectionLabel>
          <OptionRow
            title="예비 박 1마디"
            hint="시작 전에 한 마디를 먼저 세어 줘요"
            checked={config.countIn}
            onToggle={(countIn) => set({ countIn })}
          />
          <OptionRow
            title="속도 트레이너"
            hint={`${config.trainer.every}마디마다 +${config.trainer.step} BPM, 목표 ${config.trainer.target}`}
            checked={config.trainer.on}
            onToggle={(on) => set({ trainer: { ...config.trainer, on, target: Math.max(config.trainer.target, config.bpm) } })}
          >
            <ParamRow label="몇 마디마다">
              <Stepper label="마디 수" value={config.trainer.every} min={1} max={32} onChange={(every) => set({ trainer: { ...config.trainer, every } })} />
            </ParamRow>
            <ParamRow label="올릴 템포">
              <Stepper label="증가 폭" value={config.trainer.step} min={1} max={20} format={(v) => `+${v}`} onChange={(step) => set({ trainer: { ...config.trainer, step } })} />
            </ParamRow>
            <ParamRow label="목표 템포">
              <Stepper label="목표 템포" value={config.trainer.target} min={30} max={250} onChange={(target) => set({ trainer: { ...config.trainer, target } })} />
            </ParamRow>
          </OptionRow>
          <OptionRow
            title="무음 마디 훈련"
            hint={`${config.gap.play}마디 소리 · ${config.gap.mute}마디 무음 (속으로 박 유지)`}
            checked={config.gap.on}
            onToggle={(on) => set({ gap: { ...config.gap, on } })}
          >
            <ParamRow label="소리 나는 마디">
              <Stepper label="소리 마디" value={config.gap.play} min={1} max={8} onChange={(play) => set({ gap: { ...config.gap, play } })} />
            </ParamRow>
            <ParamRow label="무음 마디">
              <Stepper label="무음 마디" value={config.gap.mute} min={1} max={8} onChange={(mute) => set({ gap: { ...config.gap, mute } })} />
            </ParamRow>
          </OptionRow>
          <OptionRow
            title="첫 박 화면 깜빡임"
            hint="마디 첫 박마다 화면 테두리가 깜빡여요"
            checked={config.flash}
            onToggle={(flashOn) => set({ flash: flashOn })}
          />
          <OptionRow
            title="연습 타이머"
            hint={`${config.timer.minutes}분이 지나면 자동으로 멈춰요`}
            checked={config.timer.on}
            onToggle={(on) => set({ timer: { ...config.timer, on } })}
          >
            <ParamRow label="연습 시간">
              <Stepper label="연습 시간" value={config.timer.minutes} min={5} max={120} step={5} format={(v) => `${v}분`} width={48} onChange={(minutes) => set({ timer: { ...config.timer, minutes } })} />
            </ParamRow>
          </OptionRow>
          <OptionRow title="연습 일지 연동" hint="메트로놈을 멈출 때 연습 시간을 일지에 남길지 정해요" checked>
            <ChoiceGrid options={LINK_MODES} value={m.linkMode} onChange={m.setLinkMode} />
          </OptionRow>

          {/* 내 곡 프리셋 */}
          <SectionLabel>내 곡 프리셋</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {m.pieces.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                자주 치는 곡의 템포와 박자를 저장해 두면 한 번에 불러올 수 있어요.
              </Typography>
            )}
            {m.pieces.map((p) => {
              const selected = p.id === config.pieceId;
              return (
                <Box
                  key={p.id}
                  sx={{
                    display: 'flex', alignItems: 'center', borderRadius: '12px', border: '1px solid',
                    borderColor: selected ? 'secondary.main' : 'divider', bgcolor: 'background.paper',
                  }}
                >
                  <ButtonBase
                    onClick={() => m.applyPiece(selected ? null : p)}
                    sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', gap: 1, px: 1.5, py: 1.25, textAlign: 'left', borderRadius: '12px' }}
                  >
                    {selected && <CheckRounded fontSize="small" sx={{ color: 'secondary.main' }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{p.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.beats}/{p.beat_unit}{p.grouping ? ` · ${p.grouping}` : ''}{p.target_bpm ? ` · 목표 ${p.target_bpm}` : ''}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{p.bpm}</Typography>
                  </ButtonBase>
                  <IconButton aria-label={`${p.title} 수정`} onClick={() => setPieceDialog({ piece: p })} sx={{ mr: 0.5 }}>
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
            {pieceDirty && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => m.updatePiece(selectedPiece.id, presetOf)}
                sx={{ borderRadius: '12px' }}
              >
                "{selectedPiece.title}"에 지금 설정 저장
              </Button>
            )}
            <Button
              variant="text"
              startIcon={<AddRounded />}
              onClick={() => setPieceDialog({ preset: presetOf })}
              sx={{ alignSelf: 'flex-start' }}
            >
              지금 설정을 새 곡으로 저장
            </Button>
          </Box>
        </Box>
      </Box>

      <PieceDialog
        open={!!pieceDialog}
        piece={pieceDialog?.piece}
        preset={pieceDialog?.preset}
        onClose={() => setPieceDialog(null)}
        onSaved={(saved) => { if (pieceDialog?.preset) set({ pieceId: saved.id }); }}
      />
    </Dialog>
  );
}
